<template>
	<b-card class="widget-card border-0">
		<div class="widget-card-header">
			<h5 class="widget-card-title">
				{{ name }}
				<b-spinner v-if="loading" small label="Loading" class="ml-1" />
			</h5>
			<div class="widget-card-actions">
				<b-dropdown split size="sm" variant="secondary" :split-to="settingsPath" right>
					<template #button-content>Customize</template>
					<template v-if="popoutUrl">
						<b-dropdown-item @click="openPopout(popoutUrl, popoutWidth, popoutHeight)"><b-icon icon="box-arrow-up-right" class="mr-1" />Pop out quick settings</b-dropdown-item>
						<b-dropdown-item @click="$util.copyToClipboard(popoutUrl)"><b-icon icon="clipboard" class="mr-1" />Copy browser dock URL</b-dropdown-item>
						<b-dropdown-divider />
					</template>
					<b-dropdown-item @click="refreshWidget"><b-icon icon="arrow-clockwise" class="mr-1" />Refresh widget</b-dropdown-item>
					<slot name="extra-actions" />
				</b-dropdown>
			</div>
		</div>
		<div class="widget-card-preview" ref="preview">
			<div class="widget-card-preview-inner" :style="previewInnerStyle">
				<slot v-if="revealed" name="preview" />
			</div>
		</div>
		<b-button block variant="primary" class="mt-3" :id="getWidgetBtnId">
			<b-icon-plus-circle-fill /> Add to OBS
		</b-button>
		<b-popover :target="getWidgetBtnId" triggers="click blur" placement="bottom" custom-class="widget-url-popover">
			<b-form-group class="mb-0">
				<template #label>
					<div class="d-flex justify-content-between align-items-center">
						<span>OBS browser source URL</span>
						<a :href="widgetUrl" target="_blank" rel="noopener noreferrer" @click.prevent.stop="openPopout(widgetUrl, widgetPopoutWidth, widgetPopoutHeight)"><small>Open widget <b-icon icon="box-arrow-up-right"/></small></a>
					</div>
				</template>
				<b-input-group>
					<b-form-input readonly :value="widgetUrl" @focus="$event.target.select()" style="min-width: 300px"/>
					<b-input-group-append>
						<b-button type="button" variant="secondary" @click.capture.prevent.stop="copyWidgetUrl">
							<b-icon icon="clipboard" /><span class="d-none d-sm-inline"> Copy</span>
						</b-button>
					</b-input-group-append>
				</b-input-group>
			</b-form-group>
		</b-popover>
		<div class="widget-card-quick-settings" v-if="$slots['quick-settings']">
			<slot name="quick-settings" />
		</div>
	</b-card>
</template>

<script>

// Widgets render at their real OBS browser-source size, so the thumbnail renders them
// onto a virtual 16:9 canvas this large and scales it down to fit the preview box.
const DEFAULT_VIRTUAL_PREVIEW_WIDTH = 640;
const DEFAULT_VIRTUAL_PREVIEW_HEIGHT = 360;

// Each live preview (chat, alert, emote wall, ...) starts its own audio/physics/animation
// loops on mount, so mounting all of them at once — e.g. every card on initial page load —
// causes a burst of concurrent work. A single shared observer batches "became visible in
// this check" per callback; staggering only within that batch means a card scrolled into
// view on its own still reveals instantly, while a pile revealed together gets spread out.
const REVEAL_STAGGER_MS = 150;
const revealCallbacks = new WeakMap();
let sharedRevealObserver = null;

function getRevealObserver() {
	if(!sharedRevealObserver) {
		sharedRevealObserver = new IntersectionObserver(entries => {
			entries
				.filter(entry => entry.isIntersecting)
				.forEach((entry, i) => {
					const reveal = revealCallbacks.get(entry.target);
					if(!reveal) return;
					revealCallbacks.delete(entry.target);
					sharedRevealObserver.unobserve(entry.target);
					setTimeout(reveal, i * REVEAL_STAGGER_MS);
				});
		}, { rootMargin: '200px' });
	}
	return sharedRevealObserver;
}

export default {
	name: 'WidgetCard',
	props: {
		name: { type: String, required: true },
		widgetUrl: { type: String, required: true },
		widgetPopoutWidth: { type: Number, default: 400 },
		widgetPopoutHeight: { type: Number, default: 400 },
		settingsPath: { type: String, required: true },
		refreshType: { type: String, required: true },
		popoutUrl: { type: String, default: null },
		popoutWidth: { type: Number, default: 400 },
		popoutHeight: { type: Number, default: 320 },
		loading: { type: Boolean, default: false },
		virtualWidth: { type: Number, default: DEFAULT_VIRTUAL_PREVIEW_WIDTH },
		virtualHeight: { type: Number, default: DEFAULT_VIRTUAL_PREVIEW_HEIGHT },
	},
	data() {
		return { previewActualWidth: 0, revealed: false };
	},
	computed: {
		getWidgetBtnId() { return `widget-card-get-btn-${this._uid}`; },
		previewInnerStyle() {
			if(!this.previewActualWidth) return {};
			const scale = this.previewActualWidth / this.virtualWidth;
			return {
				width:           `${this.virtualWidth}px`,
				height:          `${this.virtualHeight}px`,
				transform:       `scale(${scale})`,
				transformOrigin: 'top left',
			};
		},
	},
	mounted() {
		this.previewActualWidth = this.$refs.preview.clientWidth;
		this.resizeObserver = new ResizeObserver(entries => {
			this.previewActualWidth = entries[0].contentRect.width;
		});
		this.resizeObserver.observe(this.$refs.preview);

		revealCallbacks.set(this.$refs.preview, () => { console.log('reveale'); this.revealed = true; });
		getRevealObserver().observe(this.$refs.preview);
	},
	beforeDestroy() {
		this.resizeObserver?.disconnect();
		revealCallbacks.delete(this.$refs.preview);
		sharedRevealObserver?.unobserve(this.$refs.preview);
	},
	methods: {
		openPopout(url, width, height) {
			window.open(url, '_blank', `directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=no,width=${width||400},height=${height||300}`);
		},
		copyWidgetUrl(event) {
			this.$util.copyToClipboard(this.widgetUrl, event.target.closest('div'));
			this.$bvToast.toast('Copied widget URL to clipboard', { title: 'Success', variant: 'success', solid: true });
		},
		refreshWidget() {
			this.$api.post(`widgetRefresh/${encodeURIComponent(this.refreshType)}`).catch(e => console.error('Refresh widget failed', e));
		},
	},
};
</script>

<style lang="scss">
@import '@/plugins/variable.scss';

.widget-url-popover {
	max-width: 420px;

	a {
		color: darken($vaporwave-green, 45%);

		&:hover {
			color: darken($vaporwave-green, 35%);
		}
	}
}

.widget-card-header {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: space-between;
	gap: 0.5rem;
	margin-bottom: 1rem;
}

.widget-card-title {
	margin: 0;
	display: flex;
	align-items: center;
	min-width: 0;
}

.widget-card-actions {
	display: flex;
	gap: 0.5rem;
	flex-shrink: 0;
}

.widget-card-preview {
	width: 100%;
	aspect-ratio: 16 / 9;
	position: relative;
	overflow: hidden;
	border-radius: 0.375rem;
	background: repeating-conic-gradient(#666 0% 25%, #999 0% 50%) 0 / 32px 32px;
	pointer-events: none;
	-webkit-user-select: none;
	-ms-user-select: none;
	user-select: none;
}

.widget-card-preview-inner {
	position: absolute;
	inset: 0;
}

.widget-card-quick-settings {
	margin-top: 1rem;
}
</style>
